import CopyButton from '@/components/buttons/CopyButton';
import Iconify from '@/components/iconify';
import { Alert, Box, IconButton, Link, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';

// ----------------------------------------------------------------------

export const MCP_DOCS_URL = 'https://docs.cakto.com.br/mcp/visao-geral';

const CLIENT_ID_PLACEHOLDER = 'SEU_CLIENT_ID';
const CLIENT_SECRET_PLACEHOLDER = 'SEU_CLIENT_SECRET';

const MONOSPACE = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

/**
 * O endpoint tem host próprio e NÃO é derivado da origem da página.
 *
 * Era derivado enquanto o plano era servir o MCP pelo mesmo host do painel.
 * Isso não se sustentou: `app.cakto.com.br` é um componente static site do
 * DigitalOcean App Platform, servido de object storage, sem processo nenhum
 * atrás para responder JSON-RPC. O servidor passou a rodar como workload no
 * cluster, atrás de ingress próprio. Derivar da origem hoje geraria snippet
 * apontando para um endereço que não existe, que foi exatamente o bug.
 */
export const MCP_ENDPOINT_URL = 'https://mcp.cakto.com.br';

/**
 * Esconde o segredo preservando os últimos 4 caracteres, que bastam para a
 * pessoa reconhecer qual chave está no snippet.
 */
function maskSecret(secret) {
  if (!secret) return secret;
  if (secret.length <= 4) return '•'.repeat(secret.length);
  return `${'•'.repeat(20)}${secret.slice(-4)}`;
}

function buildSnippets({ endpointUrl, clientId, clientSecret }) {
  const id = clientId || CLIENT_ID_PLACEHOLDER;
  const secret = clientSecret || CLIENT_SECRET_PLACEHOLDER;

  return [
    {
      label: 'Claude Code',
      code: [
        `claude mcp add --transport http cakto ${endpointUrl} \\`,
        `  --header "X-Cakto-Client-Id: ${id}" \\`,
        `  --header "X-Cakto-Client-Secret: ${secret}"`,
      ].join('\n'),
      /**
       * O segredo vai na linha de comando, então fica no histórico do shell
       * (~/.zsh_history, ~/.bash_history) e visível em `ps` para outros
       * usuários da máquina. É o jeito documentado do `claude mcp add`, mas
       * quem se importa com isso tem a alternativa do arquivo de configuração
       * nas outras abas.
       */
      shellHistoryWarning: true,
    },
    {
      label: 'Claude Desktop',
      code: JSON.stringify(
        {
          mcpServers: {
            cakto: {
              type: 'http',
              url: endpointUrl,
              headers: { 'X-Cakto-Client-Id': id, 'X-Cakto-Client-Secret': secret },
            },
          },
        },
        null,
        2
      ),
    },
    {
      label: 'Cursor / VS Code',
      code: JSON.stringify(
        {
          servers: {
            cakto: {
              type: 'http',
              url: endpointUrl,
              headers: { 'X-Cakto-Client-Id': id, 'X-Cakto-Client-Secret': secret },
            },
          },
        },
        null,
        2
      ),
    },
  ];
}

// ----------------------------------------------------------------------

CodeBlock.propTypes = {
  /** Texto que vai para a área de transferência — sempre o valor real. */
  code: PropTypes.string.isRequired,
  /** Texto exibido. Difere de `code` quando o segredo está mascarado. */
  displayCode: PropTypes.string.isRequired,
  /** Sem segredo real para esconder, o botão de revelar não aparece. */
  canReveal: PropTypes.bool,
  revealed: PropTypes.bool,
  onToggleReveal: PropTypes.func,
};

/**
 * Barra de ações ACIMA do bloco, não sobreposta a ele: com o botão de copiar
 * em position absolute ele cobria a primeira linha do comando, que é
 * justamente onde está a URL.
 *
 * O código quebra linha em vez de rolar na horizontal. Uma barra de rolagem
 * horizontal esconde metade de um comando de terminal e obriga a pessoa a
 * arrastar para conferir o que vai colar.
 */
function CodeBlock({ code, displayCode, canReveal = false, revealed = false, onToggleReveal }) {
  return (
    <Box
      sx={{
        borderRadius: 1.5,
        overflow: 'hidden',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        bgcolor: (theme) => (theme.palette.mode === 'light' ? 'grey.100' : 'grey.900'),
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={0.5}
        sx={{
          px: 1,
          py: 0.25,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        {canReveal && (
          <Tooltip title={revealed ? 'Ocultar segredo' : 'Mostrar segredo'}>
            <IconButton size="small" onClick={onToggleReveal}>
              <Iconify icon={revealed ? 'eva:eye-off-outline' : 'eva:eye-outline'} width={18} />
            </IconButton>
          </Tooltip>
        )}
        <CopyButton textToCopy={code} />
      </Stack>

      <Box
        component="pre"
        sx={{
          m: 0,
          px: 2.5,
          py: 2,
          fontSize: 13,
          lineHeight: 1.7,
          fontFamily: MONOSPACE,
          color: 'text.primary',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {displayCode}
      </Box>
    </Box>
  );
}

// ----------------------------------------------------------------------

McpConnectionGuide.propTypes = {
  /** client_id real do vendedor, quando disponível. Sem ele, o snippet usa placeholder. */
  clientId: PropTypes.string,
  /**
   * client_secret real. Só existe no instante da criação da Chave de API — a
   * listagem do backend nunca devolve o segredo de novo.
   */
  clientSecret: PropTypes.string,
  /** Esconde o aviso sobre o segredo quando quem chama já explica isso no contexto. */
  hideSecretNotice: PropTypes.bool,
  /**
   * Versão enxuta, para uso dentro de diálogo: sem a linha de endereço do
   * servidor (que já aparece dentro do snippet) e sem o alerta de "trate como
   * senha" (o diálogo de criação de chave já traz o seu próprio aviso).
   */
  compact: PropTypes.bool,
};

export default function McpConnectionGuide({
  clientId,
  clientSecret,
  hideSecretNotice = false,
  compact = false,
}) {
  const [tab, setTab] = useState(0);
  // Segredo real começa oculto. Foi um print de tela com o segredo à mostra
  // que motivou isto: quem só precisa colar o comando nunca precisa vê-lo, e
  // o botão de copiar leva o valor real de qualquer forma.
  const [revealed, setRevealed] = useState(false);
  const endpointUrl = MCP_ENDPOINT_URL;

  const snippets = useMemo(
    () => buildSnippets({ endpointUrl, clientId, clientSecret }),
    [endpointUrl, clientId, clientSecret]
  );

  /**
   * O snippet mascarado é MONTADO com o segredo já mascarado, em vez de
   * procurar o segredo cru dentro do texto final. Nas abas de JSON o texto sai
   * de JSON.stringify, então um segredo com `"`, `\` ou caractere de controle
   * apareceria escapado — a busca não encontrava, a máscara não aplicava e o
   * segredo real ficava na tela sem ninguém perceber. Montar duas vezes
   * elimina a classe inteira de falha, em vez de depender do formato do valor.
   */
  const maskedSnippets = useMemo(
    () => buildSnippets({ endpointUrl, clientId, clientSecret: clientSecret ? maskSecret(clientSecret) : clientSecret }),
    [endpointUrl, clientId, clientSecret]
  );

  const activeSnippet = snippets[tab];
  const hasRealSecret = Boolean(clientSecret);
  const displayCode = hasRealSecret && !revealed ? maskedSnippets[tab].code : activeSnippet.code;

  const showSecretNotice = !hideSecretNotice && !clientSecret;

  return (
    <Stack spacing={compact ? 2 : 3}>
      {!compact && (
        <Stack spacing={1}>
          <Typography variant="subtitle2" color="text.secondary">
            Endereço do servidor
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" sx={{ fontFamily: MONOSPACE, wordBreak: 'break-all' }}>
              {endpointUrl}
            </Typography>
            <CopyButton textToCopy={endpointUrl} />
          </Stack>
        </Stack>
      )}

      <Box>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0 } }}
        >
          {snippets.map((snippet) => (
            <Tab key={snippet.label} label={snippet.label} />
          ))}
        </Tabs>
        <Box sx={{ mt: 1.5 }}>
          <CodeBlock
            code={activeSnippet.code}
            displayCode={displayCode}
            canReveal={hasRealSecret}
            revealed={revealed}
            onToggleReveal={() => setRevealed((current) => !current)}
          />
        </Box>
      </Box>

      {hasRealSecret && !revealed && (
        <Typography variant="caption" color="text.secondary">
          O segredo está oculto para você poder copiar o comando sem expô-lo na tela. O botão de copiar leva o valor
          real.
        </Typography>
      )}

      {activeSnippet.shellHistoryWarning && (
        <Typography variant="caption" color="text.secondary">
          Este comando deixa o segredo no histórico do seu terminal. Se isso for um problema na sua máquina, use as
          abas <strong>Claude Desktop</strong> ou <strong>Cursor / VS Code</strong>, que gravam a credencial num
          arquivo de configuração em vez da linha de comando.
        </Typography>
      )}

      {showSecretNotice && (
        <Alert severity="info" icon={<Iconify icon="eva:info-outline" />}>
          Substitua <strong>{CLIENT_SECRET_PLACEHOLDER}</strong> pelo segredo da sua Chave de API. Ele aparece{' '}
          <strong>uma única vez</strong>, no momento em que a chave é criada — se você não o guardou, crie uma nova
          chave.
        </Alert>
      )}

      {!compact && (
        <Alert severity="warning">
          Sua Chave de API dá acesso de escrita à sua conta. Trate-a como senha: não a compartilhe nem a comite em
          repositório.
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary">
        Cliente MCP que só aceita um campo de autenticação? Use{' '}
        <Box component="code" sx={{ fontFamily: MONOSPACE }}>
          Authorization: Basic
        </Box>{' '}
        com o base64 de{' '}
        <Box component="code" sx={{ fontFamily: MONOSPACE }}>
          client_id:client_secret
        </Box>
        .{' '}
        <Link href={MCP_DOCS_URL} target="_blank" rel="noopener noreferrer">
          Ver documentação
        </Link>
        .
      </Typography>
    </Stack>
  );
}

export { CLIENT_ID_PLACEHOLDER, CLIENT_SECRET_PLACEHOLDER, CodeBlock, maskSecret };
